import { API_STATUS } from "src/config/constants/genericConstants";
import {
  DailyProductData,
  DepartmentId,
  ProduceMovementRecordInitialState,
  CommodityInterface,
  DailyData
} from "../interface";
import {
  setCommodities,
  getDepartmentRetailKey,
  formatCommodityData,
  formatPreviousWeekCommodityData,
  getDefaultSalesData,
  updateSalesDataWithOrderWindow,
  formatFutureWeekData,
  isPMRJITEditable,
  populateCommodities,
  getNextWeekNearestDeliveryDay,
  formatSearchResutsResponse
} from "../ProduceMovementAndRecord.utils";
import {
  calculateMasterDisplay,
  calculateOrderFactor
} from "../produceMovementAndRecord.calc";
import { getCurrentWeek, isBeforeDate } from "src/utils/getDates";

type apiStatus = "pending" | "error" | "idle";

export const initialState: ProduceMovementRecordInitialState = {
  deletedItemsIds: [],
  departmentId: DepartmentId.PRODUCE,
  blockNavigation: false,
  searchProductListData: {
    searchQuery: "",
    searchProductsLoading: false,
    searchResults: []
  },
  actualSales: null,
  forecastedSales: null,
  defaultForecastedSales: null,
  weekEndingDate: "",
  subClassIds: [],
  departments: [],
  isActive: false,
  showFullPageLoading: false,
  selectedItems: [],
  defaultDeliveryDays: [],
  defaultOrderDays: [],
  tableData: {
    salesData: {},
    commodities: {},
    previousWeekCommodities: {},
    futureWeekData: {
      totalForecastedSalesTillNextDeliveryDay: 0,
      salesData: {}
    },
    minForcastSales: null
  },
  defaultTableData: {
    salesData: {},
    commodities: {},
    previousWeekCommodities: {},
    futureWeekData: {
      totalForecastedSalesTillNextDeliveryDay: 0,
      salesData: {}
    },
    minForcastSales: null
  },
  discrepancyList: {
    status: API_STATUS.IDLE as apiStatus,
    data: []
  },
  tableDataStatus: API_STATUS.IDLE as apiStatus,
  tableDataError: null,
  tableDataErrorCount: 0,
  filteredCommodities: [],
  lastAccessedDate: null
};

export const handlePendingForFetchProduceMomentData = (
  state: ProduceMovementRecordInitialState
) => {
  state.tableDataStatus = API_STATUS.PENDING as apiStatus;
  state.searchProductListData = initialState.searchProductListData;
  state.deletedItemsIds = [];
  state.tableDataErrorCount += 1;
};

export const handleFullFillForFetchProduceMomentData = (
  state: ProduceMovementRecordInitialState,
  { payload }: any
) => {
  const {
    currentWeekData,
    carryForwardItems,
    dailySalesForWeek,
    previousWeekData,
    futureWeekData,
    deletedItems
  } = payload.data;

  const salesData = getDefaultSalesData();
  const departmentId = state.departmentId;
  const departmentRetailKey = getDepartmentRetailKey(departmentId);
  state.tableDataErrorCount -= 1;

  state.deletedItemsIds = deletedItems ?? [];

  let actualSales = 0;
  dailySalesForWeek.forEach((sale: any) => {
    salesData[sale.day].actualSales = +sale.sales[departmentRetailKey] || null;
    if (salesData[sale.day].actualSales) {
      salesData[sale.day].isActualSalesEditable = false;
    }
    actualSales += salesData[sale.day].actualSales || 0;
  });

  const department = state.departments.find(
    (department) => department.id === state.departmentId
  );
  const orderWindow = department?.orderWindow ?? [];
  state.actualSales = actualSales;

  state.lastAccessedDate = currentWeekData?.lastAccessedDate;

  if (Object.keys(salesData).length > 0) {
    state.tableData.salesData = updateSalesDataWithOrderWindow(
      salesData,
      orderWindow,
      state.weekEndingDate
    );
  }

  const previousWeekCommodityData =
    formatPreviousWeekCommodityData(previousWeekData);

  const formattedFutureWeekData = formatFutureWeekData(
    futureWeekData,
    orderWindow,
    state.weekEndingDate
  );

  state.tableData.futureWeekData = formattedFutureWeekData;
  state.defaultTableData.futureWeekData = formattedFutureWeekData;
  state.tableData.previousWeekCommodities = previousWeekCommodityData;
  state.defaultTableData.previousWeekCommodities = previousWeekCommodityData;

  // set sales data from api
  if (currentWeekData !== null) {
    let actualSales = 0;
    let forecastedSales = 0;
    let minForcastSales: number | null = null;
    currentWeekData.dailySalesData?.forEach((dailySales: any) => {
      salesData[dailySales.day].forecastSales = dailySales.forecastSales;
      salesData[dailySales.day].actualSales ??= dailySales.actualSales;

      if (dailySales.forecastSales !== null) {
        minForcastSales = Math.min(
          minForcastSales ?? Infinity,
          dailySales.forecastSales
        );
      }
      if (!isPMRJITEditable(state.weekEndingDate)) {
        salesData[dailySales.day].orderedBy = dailySales.orderedToday;
        salesData[dailySales.day].deliveredBy = dailySales.receivedToday;
      }
      actualSales += +salesData[dailySales.day].actualSales;
      forecastedSales += dailySales.forecastSales;
    });

    for (let i = 0; i < 7; i++) {
      salesData[i].orderFactor = calculateOrderFactor(
        Object.values(salesData),
        +formattedFutureWeekData.totalForecastedSalesTillNextDeliveryDay,
        i
      );
    }

    state.tableData.minForcastSales = minForcastSales;
    state.defaultTableData.minForcastSales = minForcastSales;
    state.actualSales = actualSales;
    state.forecastedSales = forecastedSales;
    state.defaultForecastedSales = forecastedSales;
  }

  // set commodities from api
  if (currentWeekData !== null && currentWeekData?.commodities?.length) {
    const [currentWeekEnd] = getCurrentWeek();
    let calculatedCommodity: {
      [key: string]: CommodityInterface;
    } = {};
    if (isBeforeDate(state.weekEndingDate, currentWeekEnd)) {
      calculatedCommodity = populateCommodities(currentWeekData.commodities);
    } else {
      const currentWeekCommodityData = formatCommodityData(
        currentWeekData?.commodities,
        carryForwardItems ?? [],
        state.tableData.minForcastSales,
        state.deletedItemsIds
      );

      const orderWindow =
        state.departments.find(
          (department) => department.id === state.departmentId
        )?.orderWindow ?? [];

      calculatedCommodity = setCommodities(
        currentWeekCommodityData,
        +state.actualSales,
        salesData,
        previousWeekCommodityData,
        orderWindow,
        state.weekEndingDate,
        false
      );
    }

    state.tableData.commodities = calculatedCommodity;
    state.defaultTableData.commodities = calculatedCommodity;
  } else if (carryForwardItems?.length) {
    // need to set the items form previous week data and update the forecasted sales and actual sales etc

    const data = carryForwardItems.map((item: any) => {
      return {
        ...item,
        dailyProductData: item.dailyData?.map((daily: any) => {
          return {
            ...daily,
            received: daily.received || null
          };
        })
      };
    });

    data.sort((itemA: any, itemB: any) => itemA?.sortOrder - itemB?.sortOrder);

    const carryForwardCommodityData = formatCommodityData(
      data,
      [],
      state.tableData.minForcastSales,
      state.deletedItemsIds
    );

    const orderWindow =
      state.departments.find(
        (department) => department.id === state.departmentId
      )?.orderWindow ?? [];

    const calculatedCommodity = setCommodities(
      carryForwardCommodityData,
      +state.actualSales,
      salesData,
      previousWeekCommodityData,
      orderWindow,
      state.weekEndingDate,
      false
    );
    state.tableData.commodities = calculatedCommodity;
    state.defaultTableData.commodities = calculatedCommodity;
  } else {
    state.tableData.commodities = initialState.tableData.commodities;
    state.tableData.previousWeekCommodities =
      initialState.tableData.previousWeekCommodities;
    state.defaultTableData.commodities =
      initialState.defaultTableData.commodities;
    state.defaultTableData.previousWeekCommodities =
      initialState.defaultTableData.previousWeekCommodities;
  }
  state.tableData.salesData = salesData;
  state.defaultTableData.salesData = salesData;
  state.tableDataStatus = API_STATUS.IDLE as apiStatus;
};

export const handleRejectForFetchProduceMomentData = (
  state: ProduceMovementRecordInitialState
) => {
  state.tableDataStatus = API_STATUS.ERROR as apiStatus;
  state.searchProductListData = initialState.searchProductListData;
  state.deletedItemsIds = [];
  state.tableDataStatus = state.tableDataErrorCount === 1 ? "error" : "pending";
  state.tableDataErrorCount -= 1;
};

export const handlePendingPMRJITConfiguration = (
  state: ProduceMovementRecordInitialState
) => {
  state.departments = [];
  state.showFullPageLoading = true;
};

export const handleFullFillPMRJITConfiguration = (
  state: ProduceMovementRecordInitialState,
  { payload }: any
) => {
  const departmentMapper = {
    PR: "Produce",
    MT: "Meat"
  };

  try {
    const {
      departments,
      DeliveryDays = [],
      orderDays = [],
      isActive = false
    } = payload.data ?? {};

    const activeDepartments = departments?.filter(
      (department: any) =>
        department.isActive && department.department in departmentMapper
    );
    state.departments = activeDepartments?.map(
      (department: any, index: number) => ({
        id: department.department,
        label:
          departmentMapper[
          department.department as keyof typeof departmentMapper
          ],
        tabIndex: index,
        canSubmit: department.canSubmit,
        vendors: department.vendors,
        orderWindow:
          department.orderWindow?.map((window: any) => ({
            orderDay: window.orderDay.split("T")[0],
            deliveryDay: window.deliveryDay.split("T")[0],
            isRecurring: window.isRecurring
          })) ?? []
      })
    );

    state.defaultDeliveryDays = DeliveryDays;
    state.defaultOrderDays = orderDays;
    state.isActive = isActive;
    state.departmentId =
      activeDepartments?.[0]?.department ?? DepartmentId.PRODUCE;
  } catch (error) {
    console.error(error);
  } finally {
    state.showFullPageLoading = false;
  }
};

export const handleRejectPMRJITConfiguration = (
  state: ProduceMovementRecordInitialState
) => {
  state.departments = [];
  state.showFullPageLoading = false;
};

export const handlePendingDiscrepancyList = (
  state: ProduceMovementRecordInitialState
) => {
  state.discrepancyList.status = API_STATUS.PENDING as apiStatus;
};

export const handleFullFillDiscrepancyList = (
  state: ProduceMovementRecordInitialState,
  { payload }: any
) => {
  state.discrepancyList.status = API_STATUS.IDLE as apiStatus;
  state.discrepancyList.data = payload.data.map((item: any) => {
    return {
      itemNumber: item.itemId,
      pmrJitQuantity: item.pmrjitQty,
      orderGuideQuantity: item.orderGuideQty,
      discrepancyReason: item.discrepancyReason
    };
  });
};

export const handleRejectDiscrepancyList = (
  state: ProduceMovementRecordInitialState
) => {
  state.discrepancyList.status = API_STATUS.ERROR as apiStatus;
};

export const updateDailySalesForDeleteAll = (
  state: ProduceMovementRecordInitialState
) => {
  state.selectedItems.forEach((itemId: string) => {
    delete state.tableData.commodities[itemId];
  });

  const deletedItemIds = [...state.deletedItemsIds];

  for (const itemId of state.selectedItems) {
    if (!deletedItemIds.includes(itemId)) {
      deletedItemIds.push(itemId);
    }
  }

  state.deletedItemsIds = deletedItemIds;
  state.selectedItems = [];
  state.blockNavigation = true;
};

export const handleFulfillSearchProductForJITorder = (
  state: any,
  action: any
) => {
  const { payload } = action;
  state.searchProductListData = {
    ...state.searchProductListData,
    searchResults: payload?.data?.length
      ? formatSearchResutsResponse(payload?.data)
      : [],
    searchProductsLoading: false
  };
};

export const handleRejectSearchProductForJITorder = (state: any) => {
  state.searchProductListData = {
    ...state.searchProductListData,
    searchResults: null,
    searchProductsLoading: false
  };
};

export const handlePendingSearchProductForJITorder = (
  state: any,
  payload: any
) => {
  state.searchProductListData = {
    ...state.searchProductListData,
    searchQuery: payload.meta.arg.searchQuery,
    searchProductsLoading: true
  };
};

export const handleClearSearchResult = (
  state: ProduceMovementRecordInitialState
) => {
  state.searchProductListData = { ...initialState.searchProductListData };
};

export const setSelectedItems = (
  state: ProduceMovementRecordInitialState,
  action: any
) => {
  state.selectedItems = action.payload;
};

export const setDepartment = (
  state: ProduceMovementRecordInitialState,
  action: any
) => {
  state.departmentId = action.payload.departmentId;
  state.tableData = initialState.tableData;
  state.defaultTableData = initialState.defaultTableData;
  state.actualSales = null;
  state.forecastedSales = null;
  state.selectedItems = [];
  state.deletedItemsIds = [];
  state.subClassIds = [];
  state.blockNavigation = false;
};

export const resetToDefault = (state: ProduceMovementRecordInitialState) => {
  state.tableData = state.defaultTableData;
  state.forecastedSales = state.defaultForecastedSales;
  state.blockNavigation = false;
};

export const resetToInitialState = () => {
  return initialState;
};

export const setBlockNavigation = (
  state: ProduceMovementRecordInitialState,
  action: any
) => {
  state.blockNavigation = action.payload;
};

export const setSubClasses = (
  state: ProduceMovementRecordInitialState,
  action: any
) => {
  state.subClassIds = action.payload;
};

export const setOrderWindow = (
  state: ProduceMovementRecordInitialState,
  action: any
) => {
  const { orderWindow } = action.payload;
  const deptIdx = state.departments.findIndex(
    (dep) => dep.id === state.departmentId
  );

  state.departments[deptIdx].orderWindow = orderWindow;

  if (Object.keys(state.tableData.salesData).length > 0) {
    const salesDataWithOrderWindow = updateSalesDataWithOrderWindow(
      { ...state.tableData.salesData },
      orderWindow,
      state.weekEndingDate
    );

    const nearestDeliveryDay = getNextWeekNearestDeliveryDay(
      orderWindow,
      state.weekEndingDate
    );

    let totalForecastedSalesTillNextDeliveryDay = 0;
    for (let i = 0; i < nearestDeliveryDay; i++) {
      totalForecastedSalesTillNextDeliveryDay +=
        state.tableData.futureWeekData.salesData[i]?.forecastSales ?? 0;
    }

    state.tableData.futureWeekData.totalForecastedSalesTillNextDeliveryDay =
      totalForecastedSalesTillNextDeliveryDay;

    Object.keys(salesDataWithOrderWindow).forEach((day: any) => {
      salesDataWithOrderWindow[day].orderFactor = calculateOrderFactor(
        salesDataWithOrderWindow,
        +totalForecastedSalesTillNextDeliveryDay,
        day
      );
    });

    state.tableData.salesData = salesDataWithOrderWindow;
    const calculatedCommodity = setCommodities(
      state.tableData.commodities,
      +state.actualSales,
      salesDataWithOrderWindow,
      state.tableData.previousWeekCommodities,
      orderWindow,
      state.weekEndingDate,
      true
    );
    state.tableData.commodities = calculatedCommodity;
  }
};
/** *********************** Refactored reducer functions *********************** */

export const setForecastSales = (
  state: ProduceMovementRecordInitialState,
  action: any
) => {
  const { day, value } = action.payload;

  const salesData: {
    [key: number]: DailyData;
  } = JSON.parse(JSON.stringify(state.tableData.salesData));
  let forecastSalesDiff = 0;
  forecastSalesDiff = +value - salesData?.[day].forecastSales;
  salesData[day].forecastSales = +value || null;
  state.forecastedSales = state.forecastedSales + forecastSalesDiff;

  let minForcastSales: number | null = null;
  for (const sales of Object.values(salesData)) {
    if (sales?.forecastSales !== null) {
      minForcastSales = Math.min(
        minForcastSales ?? Infinity,
        sales.forecastSales
      );
    }
  }

  state.tableData.minForcastSales = minForcastSales;

  const orderWindow =
    state.departments.find((department) => department.id === state.departmentId)
      ?.orderWindow ?? [];

  const commodities: {
    [key: string]: CommodityInterface;
  } = JSON.parse(JSON.stringify(state.tableData.commodities));

  if (state.departmentId === DepartmentId.MEAT) {
    for (const commodity of Object.values(state.tableData.commodities)) {
      commodity.masterDisplay = calculateMasterDisplay(
        commodity.casesPerThousand,
        minForcastSales
      );
      commodities[commodity.itemId] = commodity;
    }
  }

  Object.keys(salesData).forEach((day: any) => {
    salesData[day].orderFactor = calculateOrderFactor(
      salesData,
      +state.tableData.futureWeekData.totalForecastedSalesTillNextDeliveryDay,
      day
    );
  });

  const updatedCommodities = setCommodities(
    commodities,
    +state.actualSales,
    salesData,
    state.tableData.previousWeekCommodities,
    orderWindow,
    state.weekEndingDate,
    false
  );

  state.tableData.salesData = salesData;
  state.tableData.commodities = updatedCommodities;
  state.blockNavigation = true;
};

export const setActualSales = (
  state: ProduceMovementRecordInitialState,
  action: any
) => {
  const { day, value } = action.payload;
  const salesData: {
    [key: number]: DailyData;
  } = JSON.parse(JSON.stringify(state.tableData.salesData));
  let actualSalesDiff = 0;
  actualSalesDiff = +value - salesData?.[day].actualSales;
  salesData[day].actualSales = +value || null;
  state.actualSales = state.actualSales + actualSalesDiff;

  const orderWindow =
    state.departments.find((department) => department.id === state.departmentId)
      ?.orderWindow ?? [];

  const commodities: {
    [key: string]: CommodityInterface;
  } = JSON.parse(JSON.stringify(state.tableData.commodities));

  const updatedCommodities = setCommodities(
    commodities,
    +state.actualSales,
    salesData,
    state.tableData.previousWeekCommodities,
    orderWindow,
    state.weekEndingDate,
    false
  );

  state.tableData.salesData = salesData;
  state.tableData.commodities = updatedCommodities;
  state.blockNavigation = true;
};

export const setOrderFactor = (
  state: ProduceMovementRecordInitialState,
  action: any
) => {
  const { day, value } = action.payload;
  state.tableData.salesData[day].orderFactor = +value || null;
  const orderWindow =
    state.departments.find((department) => department.id === state.departmentId)
      ?.orderWindow ?? [];

  const updatedCommodities = setCommodities(
    state.tableData.commodities,
    +state.actualSales,
    state.tableData.salesData,
    state.tableData.previousWeekCommodities,
    orderWindow,
    state.weekEndingDate,
    false
  );

  state.tableData.commodities = updatedCommodities;

  state.blockNavigation = true;
};

export const setCasesPerThousand = (
  state: ProduceMovementRecordInitialState,
  action: any
) => {
  const {
    itemId,
    value
  }: {
    itemId: string;
    value: string;
  } = action.payload;

  const commodity = state.tableData.commodities[itemId];
  if (!commodity) return;
  const casesPerThousand = value === "" ? null : +value;
  state.tableData.commodities[itemId].casesPerThousand = casesPerThousand;
  const orderWindow =
    state.departments.find((department) => department.id === state.departmentId)
      ?.orderWindow ?? [];

  if (state.departmentId === DepartmentId.MEAT) {
    state.tableData.commodities[itemId].masterDisplay = calculateMasterDisplay(
      commodity.casesPerThousand,
      state.tableData.minForcastSales
    );
  }

  const updatedCommodities = setCommodities(
    state.tableData.commodities,
    +state.actualSales,
    state.tableData.salesData,
    state.tableData.previousWeekCommodities,
    orderWindow,
    state.weekEndingDate,
    false
  );

  state.tableData.commodities = updatedCommodities;
  state.blockNavigation = true;
};

export const setMasterDisplay = (
  state: ProduceMovementRecordInitialState,
  action: any
) => {
  const {
    itemId,
    value
  }: {
    itemId: string;
    value: string;
  } = action.payload;

  const commodity = state.tableData.commodities[itemId];
  if (!commodity) return;
  const masterDisplay = value === "" ? null : +value;
  state.tableData.commodities[itemId].masterDisplay = masterDisplay;

  const orderWindow =
    state.departments.find((department) => department.id === state.departmentId)
      ?.orderWindow ?? [];

  const updatedCommodities = setCommodities(
    state.tableData.commodities,
    +state.actualSales,
    state.tableData.salesData,
    state.tableData.previousWeekCommodities,
    orderWindow,
    state.weekEndingDate,
    false
  );

  state.tableData.commodities = updatedCommodities;

  state.blockNavigation = true;
};

export const setItemInventory = (
  state: ProduceMovementRecordInitialState,
  action: {
    payload: {
      itemId: string;
      day: number;
      value: string;
      inventoryType: "inventoryBackStore" | "inventoryCounter";
    };
  }
) => {
  const { itemId, value, day, inventoryType } = action.payload;

  const commodity = state.tableData.commodities[itemId];
  if (!commodity) return;
  const inventory = value === "" ? null : +value;
  const commodities = state.tableData.commodities;
  commodities[itemId].dailyProductData[day][inventoryType] = inventory;
  const orderWindow =
    state.departments.find((department) => department.id === state.departmentId)
      ?.orderWindow ?? [];

  const updatedCommodities = setCommodities(
    commodities,
    +state.actualSales,
    state.tableData.salesData,
    state.tableData.previousWeekCommodities,
    orderWindow,
    state.weekEndingDate,
    false
  );
  state.tableData.commodities = updatedCommodities;
  state.blockNavigation = true;
};

export const setItemReceivedValue = (
  state: ProduceMovementRecordInitialState,
  action: {
    payload: {
      itemId: string;
      value: string;
      day: number;
    };
  }
) => {
  const { itemId, value, day } = action.payload;

  const commodity = state.tableData.commodities[itemId];
  if (!commodity) return;
  const received = value === "" ? null : +value;
  state.tableData.commodities[itemId].dailyProductData[day].received = received;

  const orderWindow =
    state.departments.find((department) => department.id === state.departmentId)
      ?.orderWindow ?? [];

  const updatedCommodities = setCommodities(
    state.tableData.commodities,
    +state.actualSales,
    state.tableData.salesData,
    state.tableData.previousWeekCommodities,
    orderWindow,
    state.weekEndingDate,
    false
  );
  state.tableData.commodities = updatedCommodities;
  state.blockNavigation = true;
};

export const AddItemToProduceMovementRecord = (
  state: ProduceMovementRecordInitialState,
  action: any
) => {
  const { itemId, description, upc, isRandomWeight, subclassId, pack, size, sizeUnit } =
    action.payload;

  const previousWeekCommodities = state.tableData.previousWeekCommodities;
  const casesPerThousand =
    previousWeekCommodities[itemId]?.soldCasesPer1000 ?? null;
  const masterDisplay = previousWeekCommodities[itemId]?.masterDisplay ?? null;

  const commodity: CommodityInterface = {
    itemId,
    description,
    upc,
    isRandomWeight,
    subclassId,
    pack,
    size: `${size} ${sizeUnit}`,
    masterDisplay: state.departmentId === "MT" ? masterDisplay : null,
    casesPerThousand,
    totalCasesSold: null,
    soldCasesPer1000: null,
    sortOrder: 0,
    dailyProductData: Array.from({ length: 7 }, (_): null => null).reduce(
      (acc: { [key: number]: DailyProductData }, _, i: number) => {
        acc[i] = {
          day: i,
          productRequired: null,
          inventoryBackStore: null,
          inventoryCounter: null,
          order: null,
          received: null,
          productAvailable: null,
          overShort: null,
          casesSold: null
        };
        return acc;
      },
      {} as { [key: number]: DailyProductData }
    )
  };

  for (const [_, value] of Object.entries(state.tableData.commodities)) {
    if (value.sortOrder !== null) {
      value.sortOrder += 1;
    }
  }

  state.tableData.commodities = {
    ...state.tableData.commodities,
    [itemId]: commodity
  };

  // Remove item from deletedItemId list if it was deleted before
  const updatedDeletedItemIds = state.deletedItemsIds.filter(
    (id) => id !== itemId
  );
  state.deletedItemsIds = updatedDeletedItemIds;

  state.blockNavigation = true;
};

export const setRowData = (
  state: ProduceMovementRecordInitialState,
  action: any
) => {
  const { itemId, dailyData } = action.payload;
  const commodity = state.tableData.commodities[+itemId];
  if (!commodity) return;

  dailyData.forEach((daily: { day: number; received: number }) => {
    commodity.dailyProductData[daily.day].received = +daily.received || null;
  });

  const orderWindow =
    state.departments.find((department) => department.id === state.departmentId)
      ?.orderWindow ?? [];

  const updatedCommodities = setCommodities(
    state.tableData.commodities,
    +state.actualSales,
    state.tableData.salesData,
    state.tableData.previousWeekCommodities,
    orderWindow,
    state.weekEndingDate,
    false
  );
  state.tableData.commodities = updatedCommodities;

  state.blockNavigation = true;
};

export const setWeekEnd = (
  state: ProduceMovementRecordInitialState,
  action: any
) => {
  state.weekEndingDate = action.payload;
  state.tableData = initialState.tableData;
  state.defaultTableData = initialState.defaultTableData;
  state.actualSales = null;
  state.forecastedSales = null;
  state.selectedItems = [];
  state.deletedItemsIds = [];
  state.subClassIds = [];
  state.discrepancyList = initialState.discrepancyList;
  state.blockNavigation = false;
};

export const setPackSizeValue = (
  state: ProduceMovementRecordInitialState,
  action: any
) => {
  const {
    itemId,
    key,
    value
  }: {
    itemId: string;
    key: string;
    value: string;
  } = action.payload;

  const commodity = state.tableData.commodities[itemId];
  if (!commodity) return;

  if (key === "pack") {
    state.tableData.commodities[itemId].pack = value;
  } else if (key === "size") {
    const unit = commodity.size.split(" ")?.[1];

    const newSizeValue = `${value} ${unit}`;

    state.tableData.commodities[itemId].size = newSizeValue;
  }

  const orderWindow =
    state.departments.find((department) => department.id === state.departmentId)
      ?.orderWindow ?? [];

  const updatedCommodities = setCommodities(
    state.tableData.commodities,
    +state.actualSales,
    state.tableData.salesData,
    state.tableData.previousWeekCommodities,
    orderWindow,
    state.weekEndingDate,
    false
  );

  state.tableData.commodities = updatedCommodities;
  state.blockNavigation = true;
};

export const resetAfterSave = (state: ProduceMovementRecordInitialState) => {
  state.defaultTableData = state.tableData;
  state.defaultForecastedSales = state.forecastedSales;
  state.blockNavigation = false;
};

export const setFilteredCommodities = (
  state: ProduceMovementRecordInitialState,
  action: any
) => {
  state.filteredCommodities = action.payload;
};
